import { AxiosResponse } from "axios";
import { axiosInstance } from "./common";
import { Interview } from "./Interviews";
import { CaseNote } from "./CaseNote";

/* ===========================
   ENUMS
=========================== */

export enum InterviewMode {
  IN_PERSON = "IN_PERSON",
  VIDEO_CALL = "VIDEO_CALL",
  PHONE = "PHONE",
}

export enum TimeWindow {
  SIX_MONTHS = "6M",
  TWELVE_MONTHS = "12M",
}

export enum VerificationGroupType {
  BORROWER_IDENTITY = "BORROWER_IDENTITY",
  BORROWER_PROFILE = "BORROWER_PROFILE",
  COLLATERAL = "COLLATERAL",
  VALUATION = "VALUATION",
}

export enum VerificationStatus {
  POSITIVE = "POSITIVE",
  NEGATIVE = "NEGATIVE",
  WAIVED = "WAIVED",
  NOT_AVAILABLE = "NOT_AVAILABLE",
}

export enum StakeholderSubType {
  PRIMARY_BORROWER = "PRIMARY_BORROWER",
  CO_BORROWER = "CO_BORROWER",
}

/* ===========================
   REQUEST PARAMS
=========================== */

export interface AnalysisStepParams {
  case_id: string;
  loan_reference_ids?: string[];
}

/* ===========================
   INTERVIEW STEP
=========================== */

export interface InterviewsStepResponse {
  interview_details: Interview[];
  interview_findings?: CaseNote;
}

/* ===========================
   PORTFOLIO ANALYSIS
=========================== */

export interface PortfolioKPIs {
  persona_id: string;
  persona_name: string;
  persona_type: string;
  total_logins: number;
  rejections: number;
  sanctioned: number;
  under_review: number;
  delinquency: number;
}

export interface PortfolioDetails {
  time_window: TimeWindow;
  details: PortfolioKPIs[];
}

export interface PortfolioAnalysisStepResponse {
  portfolio_details: PortfolioDetails[];
  remarks?: CaseNote;
}

/* ===========================
   CASE OVERVIEW
=========================== */
export interface LoanDetails {
  loan_reference_id: string;
  customer_name?: string | null;
  sanctioned_amount?: number | null;
  sanctioned_date?: string | null; // ISO date string
  sanctioned_by?: string | null;
  exposure?: number | null;
  sales_manager?: string | null;
  sourcing_partner_name?: string | null;
  dpd?: number | null;
  exposure_currency: string;
}

export interface CaseOverviewStepResponse {
  loan_details: LoanDetails[];
  remarks?: CaseNote;
}


/* ===========================
   VERIFICATION DETAILS
=========================== */

export interface OnboardingDetails {
  verified_by?: string | null;
  verification_status?: VerificationStatus | null;
  verification_date?: string | null;
  remarks?: string | null;
  pre_login_valuation?: number | null;
}

export interface CurrentDetails {
  verification_status?: VerificationStatus | null;
  current_valuation?: number | null;
}

export interface VerificationDetails {
  document_type: string;
  document_id: string;
  onboarding_details: OnboardingDetails | null;
  current_details?: CurrentDetails | null;
  evidence_link?: string | null;
}

export interface VerificationGroup {
  group_name: VerificationGroupType;
  verifications: VerificationDetails[];
}

export interface LoanVerifications {
  loan_reference_id: string;
  borrower_name: string;
  verification_groups: VerificationGroup[];
}

/* ===========================
   NEW BORROWER STRUCTURE (KYC-based)
=========================== */

export interface BorrowerVerifications {
  borrower_id: string;
  borrower_name: string;
  borrower_type: StakeholderSubType;
  verification_groups: VerificationGroup[];
}

export interface BorrowerLoanVerifications {
  loan_reference_id: string;
  borrower_verifications: BorrowerVerifications[];
}

export interface BorrowerVerificationStepResponse {
  loan_verifications: BorrowerLoanVerifications[];
  remarks?: CaseNote;
}

/* ===========================
   ADDITIONAL ANALYSIS
=========================== */

export interface AdditionalAnalysisStepResponse {
  remarks?: CaseNote;
}

/* ===========================
   API IMPLEMENTATION
=========================== */

const AnalysisStepAPI = {
  getInterviewsStep: async (params: AnalysisStepParams) => {
    const response: AxiosResponse<InterviewsStepResponse> =
      await axiosInstance.get("/v1/analysis-steps/interviews", {
        params,
      });

    return response.data;
  },

  getPortfolioAnalysis: async (params: AnalysisStepParams) => {
    const response: AxiosResponse<PortfolioAnalysisStepResponse> =
      await axiosInstance.get("/v1/analysis-steps/portfolio-analysis", {
        params,
        paramsSerializer: { indexes: null },
      });

    return response.data;
  },
    getCaseOverview: async (params: AnalysisStepParams) => {
    const response: AxiosResponse<CaseOverviewStepResponse> =
      await axiosInstance.get("/v2/analysis-steps/case-overview", {
        params,
        paramsSerializer: { indexes: null },
      });

    return response.data;
  },

  getBorrowerVerifications: async (params: AnalysisStepParams) => {
    const response: AxiosResponse<BorrowerVerificationStepResponse> =
      await axiosInstance.get("/v1/analysis-steps/borrower-verifications", {
        params,
        paramsSerializer: { indexes: null },
      });

    return response.data;
  },

  getAdditionalAnalysis: async (params: AnalysisStepParams) => {
    const response: AxiosResponse<AdditionalAnalysisStepResponse> =
      await axiosInstance.get("/v1/analysis-steps/additional-analysis", {
        params,
      });

    return response.data;
  },
};

export default AnalysisStepAPI;
